import { Badge, Button, Surface, Table, Td, Th, Tr } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ErpEntity, ErpEntityState } from '@warehouse/shared';
import { Play, RefreshCw, RotateCcw, Webhook } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissionCheck } from '../../../auth/auth-store';
import { PageHeader } from '../../../components/PageHeader';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import {
  drainQueues,
  erpKeys,
  fetchErpStatus,
  fetchWebhookOverview,
  renewWebhooks,
  resyncEntity,
} from '../api';
import { EnvironmentBanner } from '../components/EnvironmentBanner';

/**
 * The sync dashboard (MODULES.md §M8: "per-entity cursor/status/errors,
 * outbox/inbox browsers, manual resync buttons, environment indicator banner").
 *
 * It is a live page: `useLiveQuery(['erp'], …)` re-reads status whenever any
 * outbox/inbox row changes, so an operator watching a stuck queue sees it clear
 * itself without touching the browser.
 */
export function SyncDashboardPage(): ReactNode {
  const { t } = useTranslation(['bcIntegration', 'common']);
  const can = usePermissionCheck();
  const queryClient = useQueryClient();
  const mayTrigger = can(['integration.sync.trigger']);

  const status = useQuery({ queryKey: erpKeys.status, queryFn: fetchErpStatus });
  const webhooks = useQuery({ queryKey: erpKeys.webhooks, queryFn: fetchWebhookOverview });
  useLiveQuery(['erp'], ['erp']);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['erp'] });
  };

  const resync = useMutation({
    mutationFn: ({ entity, mode }: { entity: ErpEntity; mode: 'delta' | 'full' }) =>
      resyncEntity(entity, mode),
    onSuccess: (_data, variables) => {
      toastSuccess(
        t('bcIntegration:toast.resyncQueued', {
          entity: t(`bcIntegration:entityNames.${variables.entity}`),
        }),
      );
      invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const drain = useMutation({
    mutationFn: drainQueues,
    onSuccess: () => {
      toastSuccess(t('bcIntegration:toast.drained'));
      invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const renew = useMutation({
    mutationFn: renewWebhooks,
    onSuccess: () => {
      toastSuccess(t('bcIntegration:toast.renewQueued'));
      invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const data = status.data;

  return (
    <>
      <PageHeader
        title={t('bcIntegration:dashboard.title')}
        subtitle={t('bcIntegration:dashboard.subtitle')}
        actions={
          mayTrigger ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                loading={drain.isPending}
                onClick={() => {
                  drain.mutate();
                }}
              >
                <Play className="size-4" aria-hidden /> {t('bcIntegration:actions.drain')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                loading={renew.isPending}
                onClick={() => {
                  renew.mutate();
                }}
              >
                <Webhook className="size-4" aria-hidden /> {t('bcIntegration:actions.renewWebhooks')}
              </Button>
            </>
          ) : null
        }
      />

      <EnvironmentBanner status={data} />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <InfoTile
          label={t('bcIntegration:status.mode')}
          value={data ? (data.mode === 'bc' ? t('bcIntegration:status.modeBc') : t('bcIntegration:status.modeNone')) : '—'}
        />
        <InfoTile label={t('bcIntegration:status.environment')} value={data?.environment ?? '—'} />
        <InfoTile label={t('bcIntegration:status.company')} value={data?.companyName ?? '—'} />
        <InfoTile
          label={t('bcIntegration:status.connection')}
          value={
            data ? (
              <Badge tone={data.connected ? 'success' : data.mode === 'null' ? 'neutral' : 'danger'} dot>
                {data.connected ? t('bcIntegration:status.connected') : t('bcIntegration:status.disconnected')}
              </Badge>
            ) : (
              '—'
            )
          }
          hint={data?.connectionError ?? undefined}
        />
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-3 xl:grid-cols-5">
        <CountTile label={t('bcIntegration:counts.outboxPending')} value={data?.counts.outboxPending} />
        <CountTile label={t('bcIntegration:counts.outboxFailed')} value={data?.counts.outboxFailed} warn />
        <CountTile label={t('bcIntegration:counts.outboxDead')} value={data?.counts.outboxDead} warn />
        <CountTile label={t('bcIntegration:counts.inboxPending')} value={data?.counts.inboxPending} />
        <CountTile label={t('bcIntegration:counts.inboxFailed')} value={data?.counts.inboxFailed} warn />
      </div>

      <Surface className="mb-6 overflow-hidden">
        <h2 className="border-b border-surface-border px-5 py-3 font-mono text-[0.8125rem] font-medium uppercase tracking-[0.14em] text-ink-muted">
          {t('bcIntegration:entities.title')}
        </h2>
        <div className="overflow-x-auto scrollbar-thin">
          <Table>
            <thead>
              <tr className="border-b border-surface-border">
                <Th scope="col">{t('bcIntegration:entities.entity')}</Th>
                <Th scope="col">{t('bcIntegration:entities.target')}</Th>
                <Th scope="col">{t('bcIntegration:entities.cursor')}</Th>
                <Th scope="col">{t('bcIntegration:entities.lastFullSync')}</Th>
                <Th scope="col">{t('bcIntegration:entities.queue')}</Th>
                <Th scope="col">{t('bcIntegration:entities.lastError')}</Th>
                {mayTrigger ? <Th scope="col">{t('bcIntegration:entities.actions')}</Th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {(data?.entities ?? []).map((entity) => (
                <EntityRow
                  key={entity.entity}
                  entity={entity}
                  mayTrigger={mayTrigger}
                  busy={resync.isPending}
                  onResync={(mode) => {
                    resync.mutate({ entity: entity.entity, mode });
                  }}
                />
              ))}
              {data && data.entities.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-sm text-ink-muted">
                    {t('bcIntegration:entities.empty')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>
      </Surface>

      <Surface className="overflow-hidden">
        <h2 className="border-b border-surface-border px-5 py-3 font-mono text-[0.8125rem] font-medium uppercase tracking-[0.14em] text-ink-muted">
          {t('bcIntegration:webhooks.title')}
        </h2>
        {webhooks.data && !webhooks.data.enabled ? (
          <p className="px-5 py-4 text-sm text-ink-muted">{t('bcIntegration:webhooks.disabled')}</p>
        ) : null}
        {webhooks.data?.enabled ? (
          <div className="overflow-x-auto scrollbar-thin">
            <Table>
              <thead>
                <tr className="border-b border-surface-border">
                  <Th scope="col">{t('bcIntegration:entities.entity')}</Th>
                  <Th scope="col">{t('bcIntegration:webhooks.resource')}</Th>
                  <Th scope="col">{t('bcIntegration:webhooks.expires')}</Th>
                  <Th scope="col">{t('bcIntegration:status.connection')}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {webhooks.data.subscriptions.map((subscription) => (
                  <Tr key={subscription.resource}>
                    <Td>
                      {subscription.entity
                        ? t(`bcIntegration:entityNames.${subscription.entity}`)
                        : subscription.resource}
                    </Td>
                    <Td className="font-mono text-xs text-ink-muted">{subscription.resource}</Td>
                    <Td>{subscription.expiresAt ? formatDateTime(subscription.expiresAt) : '—'}</Td>
                    <Td>
                      <Badge tone={subscription.registered ? (subscription.expiringSoon ? 'warning' : 'success') : 'danger'} dot>
                        {subscription.registered
                          ? subscription.expiringSoon
                            ? t('bcIntegration:webhooks.expiringSoon')
                            : t('bcIntegration:webhooks.registered')
                          : t('bcIntegration:webhooks.notRegistered')}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
                {webhooks.data.subscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-8 text-center text-sm text-ink-muted">
                      {t('bcIntegration:webhooks.empty')}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </div>
        ) : null}
      </Surface>
    </>
  );
}

function EntityRow({
  entity,
  mayTrigger,
  busy,
  onResync,
}: {
  entity: ErpEntityState;
  mayTrigger: boolean;
  busy: boolean;
  onResync: (mode: 'delta' | 'full') => void;
}): ReactNode {
  const { t } = useTranslation('bcIntegration');
  return (
    <Tr>
      <Td className="font-medium text-ink">{t(`entityNames.${entity.entity}`)}</Td>
      <Td className="font-mono text-xs text-ink-muted">{entity.target}</Td>
      <Td className="font-mono text-xs">
        {entity.cursor ? (
          <span title={entity.cursor}>{formatDateTime(entity.cursor)}</span>
        ) : (
          <span className="text-ink-muted">{t('entities.never')}</span>
        )}
      </Td>
      <Td className="text-sm">
        {entity.lastFullSyncAt ? formatRelative(entity.lastFullSyncAt) : <span className="text-ink-muted">—</span>}
      </Td>
      <Td>
        {entity.pendingInbox > 0 || entity.failedInbox > 0 ? (
          <span className="flex items-center gap-1.5">
            {entity.pendingInbox > 0 ? <Badge tone="info">{entity.pendingInbox}</Badge> : null}
            {entity.failedInbox > 0 ? <Badge tone="danger">{entity.failedInbox}</Badge> : null}
          </span>
        ) : (
          <span className="text-ink-muted">—</span>
        )}
      </Td>
      <Td className="max-w-72 truncate text-xs text-red-600" title={entity.lastError ?? undefined}>
        {entity.lastError ?? '—'}
      </Td>
      {mayTrigger ? (
        <Td>
          <span className="flex flex-wrap items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                onResync('delta');
              }}
            >
              <RefreshCw className="size-3.5" aria-hidden /> {t('actions.syncDelta')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                onResync('full');
              }}
            >
              <RotateCcw className="size-3.5" aria-hidden /> {t('actions.syncFull')}
            </Button>
          </span>
        </Td>
      ) : null}
    </Tr>
  );
}

function InfoTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: string | undefined;
}): ReactNode {
  return (
    <Surface className="p-4">
      <p className="font-mono text-[0.6875rem] uppercase tracking-[0.14em] text-ink-muted">{label}</p>
      <div className="mt-1.5 text-sm font-medium text-ink">{value}</div>
      {hint ? <p className="mt-1 text-xs text-red-600">{hint}</p> : null}
    </Surface>
  );
}

function CountTile({
  label,
  value,
  warn = false,
}: {
  label: string;
  value: number | undefined;
  warn?: boolean;
}): ReactNode {
  const highlighted = warn && (value ?? 0) > 0;
  return (
    <Surface className="p-4">
      <p className="font-mono text-[0.6875rem] uppercase tracking-[0.14em] text-ink-muted">{label}</p>
      <p className={`mt-1 font-mono text-2xl ${highlighted ? 'text-red-600' : 'text-ink'}`}>{value ?? '—'}</p>
    </Surface>
  );
}
